import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeftIcon, 
  PlusIcon, 
  CalendarIcon,
  ClockIcon,
  UserIcon,
  AcademicCapIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { staffApi } from '../staffApi';

const LessonPackDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lessonPack, setLessonPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddSessionModal, setShowAddSessionModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tutors, setTutors] = useState([]);
  const [sessionForm, setSessionForm] = useState({
    duration: '',
    subject: '',
    notes: ''
  });
  const [editForm, setEditForm] = useState({
    tutor_id: '',
    no_of_hours: '',
    subject: '',
    currency: 'USD',
    price: '',
    start_date: '',
    end_date: '',
    notes: '',
    meeting_link: ''
  });

  useEffect(() => {
    fetchLessonPackDetails();
    fetchTutors();
  }, [id]);

  const fetchLessonPackDetails = async () => {
    try {
      setLoading(true);
      const response = await staffApi.getLessonPackById(id);
      setLessonPack(response.data);
      
      // Populate edit form with current data
      if (response.data) {
        setEditForm({
          tutor_id: response.data.tutor?.id || '',
          no_of_hours: response.data.no_of_hours || '',
          subject: response.data.subject || '',
          currency: response.data.currency || 'USD',
          price: response.data.price || '',
          start_date: response.data.start_date || '',
          end_date: response.data.end_date || '',
          notes: response.data.notes || '',
          meeting_link: response.data.meeting_link || ''
        });
      }
    } catch (error) {
      console.error('Error fetching lesson pack details:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTutors = async () => {
    try {
      const response = await staffApi.getTutors();
      if (response.success) {
        setTutors(response.data.tutors || []);
      }
    } catch (error) {
      console.error('Error fetching tutors:', error);
    }
  };

  const handleAddSession = async (e) => {
    e.preventDefault();
    try {
      await staffApi.addSession(id, sessionForm);
      // Refresh the lesson pack details
      await fetchLessonPackDetails();
      setShowAddSessionModal(false);
      setSessionForm({ duration: '', subject: '', notes: '' });
    } catch (error) {
      console.error('Error adding session:', error);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await staffApi.updateLessonPack(id, editForm);
      await fetchLessonPackDetails();
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating lesson pack:', error);
    }
  };

  const handleEditCancel = () => {
    // Reset form to original data
    if (lessonPack) {
      setEditForm({
        tutor_id: lessonPack.tutor?.id || '',
        no_of_hours: lessonPack.no_of_hours || '',
        subject: lessonPack.subject || '',
        currency: lessonPack.currency || 'USD',
        price: lessonPack.price || '',
        start_date: lessonPack.start_date || '',
        end_date: lessonPack.end_date || '',
        notes: lessonPack.notes || '',
        meeting_link: lessonPack.meeting_link || ''
      });
    }
    setIsEditing(false);
  };

  const getStatusColor = (status, hoursRemaining, expiryDate) => {
    if (status === 'CANCELED' || status === 'COMPLETED') {
      return 'bg-red-100 text-red-800';
    }
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    
    if (expiry < today) {
      return 'bg-red-100 text-red-800';
    }
    
    if (hoursRemaining <= 2) {
      return 'bg-yellow-100 text-yellow-800';
    }
    
    return 'bg-green-100 text-green-800';
  };

  const getStatusText = (status, hoursRemaining, expiryDate) => {
    if (status === 'CANCELED') return 'Canceled';
    if (status === 'COMPLETED') return 'Completed';
    
    const today = new Date();
    const expiry = new Date(expiryDate);
    
    if (expiry < today) return 'Expired';
    if (hoursRemaining <= 2) return 'Low Hours';
    
    return 'Active';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getSessionStatusIcon = (status) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'SCHEDULED':
        return <CalendarIcon className="h-5 w-5 text-blue-500" />;
      case 'CANCELED':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!lessonPack) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Lesson pack not found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <button
            onClick={() => navigate('/staff/lessonpacks')}
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Lesson Packs
          </button>
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            Lesson Pack Details
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            {lessonPack.student?.name || 'N/A'} - {lessonPack.subject}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0 space-x-3">
          {!isEditing ? (
            <>
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              >
                <PencilIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Edit Lesson Pack
              </button>
              <button
                type="button"
                onClick={() => setShowAddSessionModal(true)}
                className="inline-flex items-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Add Session
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={handleEditSubmit}
                className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-600"
              >
                <CheckIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Save Changes
              </button>
              <button
                type="button"
                onClick={handleEditCancel}
                className="inline-flex items-center rounded-md bg-gray-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-600"
              >
                <XMarkIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <UserIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Student</dt>
                  <dd className="text-lg font-medium text-gray-900">{lessonPack.student?.name || 'N/A'}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <AcademicCapIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Tutor</dt>
                  <dd className="text-lg font-medium text-gray-900">{lessonPack.tutor?.name || 'No tutor assigned'}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Hours Remaining</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {lessonPack.hours_remaining != null ? `${lessonPack.hours_remaining} h` : '—'}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CurrencyDollarIcon className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Total Value</dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {formatCurrency(lessonPack.price, lessonPack.currency)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Lesson Pack Information */}
        <div className="lg:col-span-1">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Lesson Pack Information</h3>
            
            {!isEditing ? (
              <dl className="space-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Package Size</dt>
                  <dd className="mt-1 text-sm text-gray-900">{lessonPack.package_size}h</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Subject</dt>
                  <dd className="mt-1 text-sm text-gray-900">{lessonPack.subject}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(lessonPack.start_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">End Date</dt>
                  <dd className="mt-1 text-sm text-gray-900">{formatDate(lessonPack.end_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(lessonPack.status, lessonPack.hours_remaining ?? 0, lessonPack.expiry_date)}`}>
                      {getStatusText(lessonPack.status, lessonPack.hours_remaining ?? 0, lessonPack.expiry_date)}
                    </span>
                  </dd>
                </div>
                {lessonPack.notes && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Notes</dt>
                    <dd className="mt-1 text-sm text-gray-900">{lessonPack.notes}</dd>
                  </div>
                )}
                {lessonPack.meeting_link && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Meeting Link</dt>
                    <dd className="mt-1">
                      <a 
                        href={lessonPack.meeting_link} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-indigo-600 hover:text-indigo-500"
                      >
                        Join Meeting
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            ) : (
              <form onSubmit={handleEditSubmit} className="space-y-4">
                <div>
                  <label htmlFor="tutor_id" className="block text-sm font-medium text-gray-700">
                    Assigned Tutor
                  </label>
                  <select
                    name="tutor_id"
                    id="tutor_id"
                    value={editForm.tutor_id}
                    onChange={handleEditInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  >
                    <option value="">Select tutor</option>
                    {tutors.map((tutor) => (
                      <option key={tutor.id} value={tutor.id}>
                        {tutor.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    id="subject"
                    value={editForm.subject}
                    onChange={handleEditInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter subject"
                  />
                </div>

                <div>
                  <label htmlFor="no_of_hours" className="block text-sm font-medium text-gray-700">
                    Number of Hours
                  </label>
                  <input
                    type="number"
                    name="no_of_hours"
                    id="no_of_hours"
                    value={editForm.no_of_hours}
                    onChange={handleEditInputChange}
                    min="1"
                    step="0.5"
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Enter number of hours"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="currency" className="block text-sm font-medium text-gray-700">
                      Currency
                    </label>
                    <select
                      name="currency"
                      id="currency"
                      value={editForm.currency}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    >
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                      <option value="CAD">CAD (C$)</option>
                      <option value="AUD">AUD (A$)</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="price" className="block text-sm font-medium text-gray-700">
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      id="price"
                      value={editForm.price}
                      onChange={handleEditInputChange}
                      min="0"
                      step="0.01"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                      placeholder="Enter price"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      id="start_date"
                      value={editForm.start_date}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                      End Date
                    </label>
                    <input
                      type="date"
                      name="end_date"
                      id="end_date"
                      value={editForm.end_date}
                      onChange={handleEditInputChange}
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="meeting_link" className="block text-sm font-medium text-gray-700">
                    Meeting Link
                  </label>
                  <input
                    type="url"
                    name="meeting_link"
                    id="meeting_link"
                    value={editForm.meeting_link}
                    onChange={handleEditInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="https://meet.google.com/..."
                  />
                </div>

                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    id="notes"
                    rows={3}
                    value={editForm.notes}
                    onChange={handleEditInputChange}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    placeholder="Additional notes about this lesson pack..."
                  />
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Session History */}
        <div className="lg:col-span-2">
          <div className="bg-white shadow rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Session History</h3>
            {lessonPack.sessions && lessonPack.sessions.length > 0 ? (
              <div className="space-y-4">
                {(lessonPack.sessions || []).map((session) => (
                  <div key={session.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-3">
                        {getSessionStatusIcon(session.status)}
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-900">{session.subject}</h4>
                            <span className="text-xs text-gray-500">
                              {session.duration > 0 ? `${session.duration}h` : 'Scheduled'}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-1">
                            {formatDateTime(session.date)} • {session.tutor_name}
                          </p>
                          {session.notes && (
                            <p className="text-sm text-gray-700 mt-2">{session.notes}</p>
                          )}
                        </div>
                      </div>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        session.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                        session.status === 'SCHEDULED' ? 'bg-blue-100 text-blue-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {session.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <ClockIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No sessions yet</h3>
                <p className="mt-1 text-sm text-gray-500">Sessions will appear here once they are added.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Session Modal */}
      {showAddSessionModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-end justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowAddSessionModal(false)}></div>
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleAddSession}>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Add Session</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="duration" className="block text-sm font-medium text-gray-700">
                        Duration (hours)
                      </label>
                      <input
                        type="number"
                        id="duration"
                        value={sessionForm.duration}
                        onChange={(e) => setSessionForm({...sessionForm, duration: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        value={sessionForm.subject}
                        onChange={(e) => setSessionForm({...sessionForm, subject: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        required
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="notes" className="block text-sm font-medium text-gray-700">
                        Notes
                      </label>
                      <textarea
                        id="notes"
                        rows={3}
                        value={sessionForm.notes}
                        onChange={(e) => setSessionForm({...sessionForm, notes: e.target.value})}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setShowAddSessionModal(false)}
                    className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700"
                  >
                    Add Session
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonPackDetail;
